import { Button, Group, Select, Space, Table } from '@mantine/core';
import { IconPencilPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { DataTable } from '../../../components/DataTable';
import { KeyedRow } from '../../../components/KeyedRow';
import { PageHeader } from '../../../components/PageHeader';
import { SearchField } from '../../../components/SearchField';
import { Title } from '../../../components/Title';
import { useModal } from '../../../hooks/useModal';
import { usePagination } from '../../../hooks/usePagination';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { whiteIconButtonStyle } from '../../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { query } from '../../../util/api/query';
import { organisationNameToUrl } from '../../../util/convertToUrl';
import { formatToDateString } from '../../../util/formatting';
import type { DropdownOptionDto } from '../../../util/types/DropdownOptionDto';
import type { SelectableBranchDto } from '../../../util/types/SelectableBranchDto';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { StockAccountDto } from '../types/StockAccountDto';
import { NewStockAccountModal } from './NewStockAccountModal';

export function StockAppAccountsView() {
	const pagination = usePagination();

	const [searchQuery, setSearchQuery] = useState('');
	const [organisationId, setOrganisationId] = useState<string | null>(null);
	const [areaOrBranchKey, setAreaOrBranchKey] = useState<string | null>(null);

	const newModal = useModal();

	const organisationsQuery = query<DropdownOptionDto[]>('organisation/options');

	const recordsParams = useUrlParams({
		searchQuery: searchQuery,
		organisationId: organisationId ?? '',
		areaOrBranchName: areaOrBranchKey ? optionKeyToName(areaOrBranchKey) : '',
		size: pagination.value.pageSize,
		number: pagination.value.pageNumber,
	});

	const countParams = useUrlParams({
		searchQuery: searchQuery,
		organisationId: organisationId ?? '',
		areaOrBranchName: areaOrBranchKey ? optionKeyToName(areaOrBranchKey) : '',
	});

	const accountsQuery = query<StockAccountDto[]>(
		`stock/accounts/all?${recordsParams}`,
	);

	const accountsCountQuery = query<number>(
		`stock/accounts/count?${countParams}`,
	);

	const triggerListUpdate = () => {
		accountsQuery.remove();
		accountsCountQuery.remove();

		accountsQuery.refetch();
		accountsCountQuery.refetch();
	};

	return (
		<>
			<NewStockAccountModal
				close={newModal.close}
				opened={newModal.isOpen}
				triggerListUpdate={triggerListUpdate}
			/>
			<PageHeader title={'Stock App Accounts'} hideFiltering>
				<Group>
					<SearchField
						placeholder={'Search usernames...'}
						onDelayComplete={(value) => setSearchQuery(value)}
					/>
					<OrganisationSelector
						organisations={organisationsQuery.data?.value}
						organisationId={organisationId}
						setOrganisationId={(value) => {
							setOrganisationId(value);
							setAreaOrBranchKey(null);
						}}
					/>
					<BranchOrAreaSelector
						organisationName={
							organisationsQuery.data?.value.find(
								(o) => o.id === organisationId,
							)?.name
						}
						areaOrBranchKey={areaOrBranchKey}
						setAreaOrBranchKey={setAreaOrBranchKey}
					/>
					<Button
						{...whiteIconButtonStyle}
						data-cy={'create-iconButton'}
						onClick={() => newModal.open()}
					>
						<IconPencilPlus {...defaultIconStyle} />
					</Button>
				</Group>
			</PageHeader>
			<Space h={20} />
			<DataTable
				pagination={pagination}
				totalItems={accountsCountQuery.data?.value}
				isRowsLoading={accountsQuery.isLoading}
				isPaginationLoading={accountsCountQuery.isLoading}
				rows={pagination.value.pageSize}
				maxHeight={'76vh'}
				minHeight={'62vh'}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>
							<Title label={'First Name'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Last Name'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Username'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Email'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Branch'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Organisation'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Account Created'} />
						</Table.Th>
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{accountsQuery.data?.value.map((account) => (
						<KeyedRow key={`${account.organisationName}-${account.id}`}>
							<Table.Td>{undefinedCheck(account.firstName)}</Table.Td>
							<Table.Td>{undefinedCheck(account.lastName)}</Table.Td>
							<Table.Td>{undefinedCheck(account.username)}</Table.Td>
							<Table.Td>{undefinedCheck(account.email)}</Table.Td>
							<Table.Td>{undefinedCheck(account.branchName)}</Table.Td>
							<Table.Td>{undefinedCheck(account.organisationName)}</Table.Td>
							<Table.Td>{formatToDateString(account.dateCreated)}</Table.Td>
						</KeyedRow>
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}

interface OrganisationSelectorProps {
	organisations?: DropdownOptionDto[];
	organisationId: string | null;
	setOrganisationId: (value: string | null) => void;
}

function OrganisationSelector({
	organisations,
	organisationId,
	setOrganisationId,
}: OrganisationSelectorProps) {
	return (
		<Select
			placeholder={'Filter by organisation...'}
			variant={'borderless-filled'}
			data={organisations?.map((o) => ({ label: o.name, value: o.id }))}
			value={organisationId}
			onChange={setOrganisationId}
			searchable
			clearable
			data-cy={'organisation-filter'}
		/>
	);
}

interface BranchOrAreaSelectorProps {
	organisationName?: string;
	areaOrBranchKey: string | null;
	setAreaOrBranchKey: (value: string | null) => void;
}

function BranchOrAreaSelector({
	organisationName,
	areaOrBranchKey,
	setAreaOrBranchKey,
}: BranchOrAreaSelectorProps) {
	// The branches live in the selected organisation's tenant database, so they can
	// only be listed once an organisation has been chosen.
	const branchesQuery = query<SelectableBranchDto>(
		`${organisationNameToUrl(organisationName ?? '')}/branches/options`,
		!!organisationName,
	);

	// The filter matches by name, so branches (or areas) sharing a name are the same
	// filter - deduplicate them to keep the option keys unique.
	const areaNames = Array.from(
		new Set(branchesQuery.data?.value.areaNames ?? []),
	);
	const branchNames = Array.from(
		new Set(branchesQuery.data?.value.branches.map((b) => b.name) ?? []),
	);

	return (
		<Select
			placeholder={
				organisationName
					? 'Filter by branch or area...'
					: 'Select an organisation first...'
			}
			variant={'borderless-filled'}
			data={[
				{
					group: 'Areas',
					items: areaNames.map((name) => ({
						label: name,
						value: toOptionKey('area', name),
					})),
				},
				{
					group: 'Branches',
					items: branchNames.map((name) => ({
						label: name,
						value: toOptionKey('branch', name),
					})),
				},
			]}
			value={areaOrBranchKey}
			onChange={setAreaOrBranchKey}
			disabled={!organisationName}
			searchable
			clearable
			data-cy={'branch-filter'}
		/>
	);
}

// An area and a branch can share a name, and option values must be unique, so
// options are keyed by kind and decoded back to a plain name for the API filter.
function toOptionKey(kind: 'area' | 'branch', name: string) {
	return `${kind}:${name}`;
}

function optionKeyToName(key: string) {
	return key.substring(key.indexOf(':') + 1);
}
